import {useLingui} from '@lingui/react/macro';
import {View, StyleSheet, Text, Pressable, ScrollView} from 'react-native';
import {Svg, Path} from 'react-native-svg';
import {useEffect, useRef} from 'react';
import {useTree} from '@headless-tree/react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate, useParams} from 'extensions/navigation';
import {load} from 'utils/mediafire';
import {theme} from 'config/themes';
import {colors, vectors} from 'config/styles';
import {
  syncDataLoaderFeature,
  hotkeysCoreFeature,
  selectionFeature,
} from '@headless-tree/core';
import {useDragDropTarget} from 'interface/hooks/useDragDropTarget';
import {useModalCreateFolder} from 'interface/hooks/useModalCreateFolder';
import {FolderTreeItem} from './FolderTreeItem';
import {DropFrame} from 'interface/base/DropFrame';

import * as app from 'store/slices/app';
import * as files from 'store/slices/files';
import type {FilesItem} from 'store/slices/files/types';

export function FolderTree() {
  const {t} = useLingui();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {folderKey} = useParams<"folderKey">();
  const isPremium = useSelector(app.selectors.isPremium);
  const views = useSelector(files.selectors.getViews);
  const items = useSelector(files.selectors.getItems);
  const sort = useSelector(files.selectors.getSort);
  const filter = useSelector(files.selectors.getFilter);
  const containerRef = useRef(null);
  const loadedItemsRef = useRef<Set<string>>(new Set(['myfiles']));
  const createFolder = useModalCreateFolder('myfiles');
  const {isDropping} = useDragDropTarget({
    ref: containerRef,
    targetId: 'myfiles',
    targetName: 'My Files',
  });
  
  const tree = useTree<FilesItem>({
    rootItemId: 'myfiles',
    getItemName: (item) => item.getItemData()?.name || '',
    isItemFolder: (item) => item.getItemData()?.type === 'folder',
    initialState: {
      focusedItem: 'myfiles',
    },
    indent: 20,
    dataLoader: {
      getItem: (itemId: string) => items?.[itemId],
      getChildren: (itemId: string) => {
        // Load data if not cached and hasn't been loaded before
        if (!views?.[itemId] && !loadedItemsRef.current.has(itemId)) {
          loadedItemsRef.current.add(itemId);
          load(navigate, dispatch, itemId, false, sort, filter, true);
        }
        // Always return cached data
        return views?.[itemId]?.folders || [];
      },
    },
    features: [
      syncDataLoaderFeature,
      hotkeysCoreFeature,
      selectionFeature,
    ],
  });

  const treeItems = tree.getItems();

  // Fetch root folder
  useEffect(() => {
    load(navigate, dispatch, 'myfiles', false, sort, filter, true);
  }, [navigate, dispatch, sort, filter]);

  // Rebuild tree when views change
  useEffect(() => {
    tree.rebuildTree();
  }, [tree, views]);

  // Set focus to current folder and expand parent folders
  useEffect(() => {
    if (folderKey && tree) {
      // Find the item to focus
      const itemToFocus = tree.getItems().find(item => item.getId() === folderKey);
      if (itemToFocus) {
        // Get the path to this item to expand parent folders
        const parentFolders = [];
        let currentItem = itemToFocus;
        
        // Traverse up the tree to find all parent folders
        while (currentItem.getItemMeta().level > 0) {
          const parentLevel = currentItem.getItemMeta().level - 1;
          const parentItem = tree.getItems().find(item => 
            item.getItemMeta().level === parentLevel && 
            currentItem.getItemMeta().parentId === item.getId()
          );
          
          if (parentItem) {
            parentFolders.unshift(parentItem);
            currentItem = parentItem;
          } else {
            break;
          }
        }
        
        // Expand all parent folders
        parentFolders.forEach(folder => {
          if (folder.isFolder() && !folder.isExpanded()) {
            folder.expand();
          }
        });
        
        // Set focus to the current folder
        itemToFocus.setFocused();
        tree.updateDomFocus();
      }
    }
  }, [folderKey, tree, views]);

  return (
    <View 
      style={styles.container} 
      {...tree.getContainerProps()}>
      <Pressable
        style={styles.header}
        ref={containerRef}
        onPress={() => navigate('/folder/myfiles')}>
        <Svg viewBox="0 0 20 20" style={styles.headerIcon} width={16} height={16}>
          <Path d={vectors.icons.folder} fill={colors.neutral.mid} fillOpacity={0.5}/>
        </Svg>
        <Text style={styles.headerText}>My Folders</Text>
        {isDropping && <DropFrame stroke="small" />}
      </Pressable>
      {views?.myfiles && treeItems?.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyText}>{t`You don't have any folders`}</Text>
          <Pressable 
            style={styles.createButton}
            onPress={createFolder.open}>
            <Text style={styles.createButtonText}>{t`Create a folder`}</Text>
          </Pressable>
        </View>
      ) : (
        <ScrollView
          style={styles.scrollContainer}
          showsVerticalScrollIndicator={false}>
          <ScrollView
            horizontal={true}
            showsHorizontalScrollIndicator={false}>
            <View>
              {tree.getItems().map(item => (
                <FolderTreeItem
                  key={item.getId()}
                  item={item}
                  isPremium={isPremium}
                  updateDomFocus={tree.updateDomFocus}
                />
              ))}
            </View>
          </ScrollView>
        </ScrollView>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    minWidth: 200,
    maxWidth: 300,
    width: '20%',
    height: '100%',
    backgroundColor: theme.files.breadcrumbs.fill,
    borderRightWidth: 1,
    borderColor: theme.files.breadcrumbs.border,
    flexDirection: 'column',
    position: 'relative',
  },
  header: {
    height: 42,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignContent: 'center',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderColor: theme.files.breadcrumbs.border,
    backgroundColor: theme.files.breadcrumbs.fill,
    paddingHorizontal: 16,
    marginBottom: 2,
  },
  scrollContainer: {
    flex: 1,
    paddingBottom: 16,
  },
  headerIcon: {
    marginRight: 8,
  },
  headerText: {
    fontSize: 12,
    color: theme.files.breadcrumbs.text,
  },
  emptyContainer: {
    flex: 1,
    height: '100%',
    width: '100%',
    padding: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    fontSize: 14,
    color: theme.files.breadcrumbs.text,
    marginBottom: 4,
  },
  createButton: {
    padding: 8,
  },
  createButtonText: {
    fontSize: 14,
    color: colors.neutral.mid,
    textDecorationLine: 'underline',
  },
}); 